/** Métadonnées publiées par `doc.capabilities.get()` pour une opération. */
export interface SuperDocOperationCapability {
  available?: boolean;
  tracked?: boolean;
  dryRun?: boolean;
}

/** Verdict d'un parcours fournisseur réellement exécuté dans Chromium. */
export type SuperDocRuntimeVerdict = 'supported' | 'unsupported';

/** Ce qu'une mutation réussie sous `suggesting` a effectivement produit. */
export type SuperDocTrackedMutationCompatibility = 'tracked' | 'direct' | 'unsupported';

/**
 * Gestes agent déjà mesurés sous suivi dans Chromium.
 *
 * Ils couvrent les écritures canoniques du Canvas ; une carte qui les omet ou
 * les réduit à `direct` ne peut donc pas faire passer un upgrade silencieux.
 */
export const OPERATIONS_SUIVIES_OBLIGATOIRES = [
  'insert',
  'create.paragraph',
  'replace',
  'lists.create',
  'styles.paragraph.setStyle',
] as const;

/**
 * Mesures contextuelles que `capabilities.get()` ne peut pas exprimer.
 *
 * Une opération `replace` disponible ne prouve ni le remplacement du corps,
 * ni son régime direct/suivi ; de même, `mutations.preview` n'est pas une
 * entrée plate dans le catalogue d'opérations. Ce relevé est versionné avec la
 * carte de capacités pour que les futures surfaces agent partent d'un parcours
 * Chromium observé plutôt que de types ou d'une absence d'exception.
 */
export interface SuperDocRuntimeQualification {
  projections: Readonly<Record<'final' | 'original' | 'redline', SuperDocRuntimeVerdict>>;
  mutationPlans: {
    previewAvailable: boolean;
    applyAvailable: boolean;
    supportedStepOps: readonly string[];
    executedStepOps: readonly string[];
  };
  bodyReplacement: {
    direct: { verdict: SuperDocRuntimeVerdict; outcome: string | null; failureCode: string | null };
    tracked: { verdict: SuperDocRuntimeVerdict; outcome: string | null; failureCode: string | null };
  };
  trackedMutations: Readonly<Record<string, SuperDocTrackedMutationCompatibility>>;
}

/** Snapshot focalisé sur les seules opérations dont dépend le catalogue Hector. */
export interface SuperDocCapabilitySnapshot {
  superdocVersion: string;
  measuredAt: string;
  operations: Readonly<Record<string, SuperDocOperationCapability>>;
  /** Absent seulement des snapshots historiques ; la garde courante le refuse. */
  runtimeQualification?: SuperDocRuntimeQualification;
}

export interface CanvasCapabilityRequirement {
  name: string;
  requiredOperations: readonly string[];
  reviewedOperations: readonly string[];
}

export type SuperDocCapabilityContractIssue = {
  verb: string | null;
  field:
    | 'snapshot'
    | 'superdocVersion'
    | 'requiredOperations'
    | 'reviewedOperations'
    | 'runtimeQualification';
  operation: string | null;
  problem:
    | 'baseline_empty'
    | 'version_mismatch'
    | 'not_required_by_verb'
    | 'operation_missing'
    | 'operation_unavailable'
    | 'runtime_qualification_missing'
    | 'runtime_qualification_invalid'
    | 'runtime_requirement_unmet';
};

interface ValidateSuperDocCapabilityContractInput {
  catalogue: readonly CanvasCapabilityRequirement[];
  capabilitySnapshot: unknown;
  pinnedSuperDocVersion: string;
  /** Une sonde live d'opérations ne produit pas forcément les parcours Chromium complets. */
  requireRuntimeQualification?: boolean;
}

/**
 * Les opérations que SuperDoc déclare INDISPONIBLES et qui fonctionnent — trois
 * à ce jour, chacune adossée à une mesure, jamais à une intuition.
 *
 * ## Pourquoi cette liste existe
 *
 * La garde de capacité (`operationsManquantes`, `verbesExecutables`) refuse un
 * verbe dont une opération requise n'est pas `available:true`. Elle a été écrite
 * pour ça : la devinette `typeof doc.X === 'function'` avait laissé
 * `undo_changes` viser `doc.undo`, une méthode inexistante, pendant toute sa
 * mise en service. Le principe reste bon.
 *
 * Mais `create.image` prend la garde à revers, dans l'autre sens : le moteur la
 * déclare indisponible **et l'exécute**. C'est par elle que l'en-tête du cabinet
 * se pose aujourd'hui en production, sur ce même runtime.
 *
 * ## La mesure, pas l'opinion
 *
 * Relevé du 2026-09-06, `superdoc@2.6.0`, `tests/ws-mocked/mesure-en-tete-cabinet.spec.ts` :
 *
 * - déclaration : `{ available: false, tracked: false, dryRun: false, reasons: ['OPERATION_UNAVAILABLE'] }`
 * - surface : `doc.create.image` est une `function`
 * - effet, jugé sur l'OOXML réexporté : pose A → `word/header1.xml` embarque
 *   `rId1` → `media/image1.png` (40×20) ; remplacement B → il embarque `rId2` →
 *   `media/image2.png` (90×30). L'ancienne partie est détachée puis supprimée.
 *
 * Le refus n'est donc pas une incapacité du moteur : `create.image` refuse tout
 * ce qui n'est pas une data URI base64 (« create.image currently requires a
 * base64 data URI »), et `canvasLetterhead.ts` ne lui en donne jamais d'autre.
 *
 * ## `images.delete` (verbe `edit_image`)
 *
 * Relevé du 2026-10-12, `superdoc@2.15.1`, `tests/ws-mocked/mesure-images-du-corps.spec.ts`,
 * sur une image flottante seule dans son paragraphe du corps :
 *
 * - déclaration : `{ available: false, tracked: false }`
 * - en direct (éditeur en `editing` comme en `suggesting`) : `success: true`, une
 *   entrée d'historique, l'image quitte `images.list`, et le fichier exporté perd
 *   son `w:drawing` et la relation de l'image ;
 * - en suivi : `success: true`, une révision `structural-image-deletion` dans
 *   `trackChanges.list`, le dessin reste au fichier sous `w:del`, et rejeter la
 *   révision rend l'image à `images.list`.
 *
 * ## `images.replaceSource` (verbe `edit_image`, remplacement)
 *
 * Relevé du 2026-10-12, `superdoc@2.15.1`, même sonde (M7, M9), avec une data URI PNG puis JPEG :
 *
 * - déclaration : `{ available: false, tracked: false, reasons: ['OPERATION_UNAVAILABLE'] }`
 * - en direct : `success: true`, une entrée d'historique, même `sdImageId`, même taille et même placement, et le
 *   fichier exporté pointe un média neuf dont les octets sont ceux de la source ;
 * - en suivi : `success: true`, une révision `structural-image-replacement`, l'ancien dessin sous `w:del` et le
 *   nouveau sous `w:ins`.
 *
 * ## Comment cette liste se retire toute seule
 *
 * `superdocCapabilityContract.integration.test.ts` (navigateur) échoue le jour où le snapshot
 * mesuré déclare l'une de ces opérations `available:true` : l'exception n'a plus
 * lieu d'être, et le test l'exige. Ne jamais y ajouter une entrée sans le relevé
 * qui la justifie — une entrée non mesurée rouvre exactement le trou que la
 * garde ferme.
 */
export const OPERATIONS_DECLAREES_A_TORT_INDISPONIBLES: readonly string[] = [
  'create.image',
  'images.delete',
  'images.replaceSource',
];

/**
 * `true` quand l'opération est déclarée indisponible à tort, donc utilisable.
 *
 * Volontairement une fonction plutôt qu'un `Set` exporté : les deux gardes qui
 * la consultent vivent dans des apps différentes (le contrat ci-dessous, et
 * `capacitesDuDocument.ts` côté web), et une seule porte d'entrée rend
 * impossible qu'elles divergent.
 */
export function estDeclareeATortIndisponible(operation: string): boolean {
  return OPERATIONS_DECLAREES_A_TORT_INDISPONIBLES.includes(operation);
}

/**
 * Teste une propriété propre sans exiger la lib ES2022.
 *
 * `shared-types` est compilé directement par l'add-in Word, dont la cible reste
 * ES2020. La forme prototype conserve la défense contre les propriétés héritées
 * sans rendre le contrat de capacités incompatible avec ce consommateur.
 */
function hasOwn(object: object, property: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(object, property);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nullableString(value: unknown): boolean {
  return value === null || typeof value === 'string';
}

function sortedOperationNames(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((operation) => typeof operation === 'string' && operation.length > 0)) {
    return null;
  }
  const sorted = [...value].sort();
  return sorted.some((operation, index) => index > 0 && operation === sorted[index - 1]) ? null : sorted;
}

/**
 * Compare le catalogue Hector au relevé runtime versionné de SuperDoc.
 *
 * `tracked` est volontairement hors du verdict : cette métadonnée statique a
 * déjà divergé du régime réellement obtenu. La disponibilité, elle, détermine
 * si le verbe peut être proposé sans échouer à chaque appel.
 *
 * La qualification runtime est opt-in pour conserver la sonde live focalisée
 * sur `capabilities.get()` ; le writer du snapshot et la garde installée la
 * rendent obligatoirement active.
 */
export function validateSuperDocCapabilityContract({
  catalogue,
  capabilitySnapshot,
  pinnedSuperDocVersion,
  requireRuntimeQualification = false,
}: ValidateSuperDocCapabilityContractInput): SuperDocCapabilityContractIssue[] {
  const issues: SuperDocCapabilityContractIssue[] = [];

  if (!isRecord(capabilitySnapshot)) {
    issues.push({ verb: null, field: 'snapshot', operation: null, problem: 'runtime_qualification_invalid' });
    return issues;
  }

  const operations = capabilitySnapshot.operations;
  if (!isRecord(operations)) {
    issues.push({ verb: null, field: 'snapshot', operation: 'operations', problem: 'runtime_qualification_invalid' });
  } else {
    if (Object.keys(operations).length === 0) {
      issues.push({ verb: null, field: 'snapshot', operation: null, problem: 'baseline_empty' });
    }
    for (const definition of catalogue) {
      const required = new Set(definition.requiredOperations);
      for (const operation of definition.requiredOperations) {
        verifierOperation(issues, operations, definition.name, 'requiredOperations', operation);
      }
      for (const operation of definition.reviewedOperations) {
        if (!required.has(operation)) {
          issues.push({
            verb: definition.name,
            field: 'reviewedOperations',
            operation,
            problem: 'not_required_by_verb',
          });
        }
        verifierOperation(issues, operations, definition.name, 'reviewedOperations', operation);
      }
    }
  }

  if (capabilitySnapshot.superdocVersion !== pinnedSuperDocVersion) {
    issues.push({ verb: null, field: 'superdocVersion', operation: null, problem: 'version_mismatch' });
  }
  if (requireRuntimeQualification) verifierQualificationRuntime(issues, capabilitySnapshot);

  return issues;
}

function verifierOperation(
  issues: SuperDocCapabilityContractIssue[],
  operations: Readonly<Record<string, unknown>>,
  verb: string,
  field: 'requiredOperations' | 'reviewedOperations',
  operation: string,
): void {
  if (!hasOwn(operations, operation)) {
    issues.push({ verb, field, operation, problem: 'operation_missing' });
    return;
  }
  const capability = operations[operation];
  if (!isRecord(capability) || !hasOwn(capability, 'available') || capability.available !== true) {
    // Une opération mesurée « indisponible ET fonctionnelle » ne rend pas le
    // contrat invalide : elle serait sinon impossible à déclarer au catalogue,
    // et le snapshot lui-même refuserait de s'écrire. Voir le TSDoc de
    // `OPERATIONS_DECLAREES_A_TORT_INDISPONIBLES` pour la mesure qui l'autorise.
    if (estDeclareeATortIndisponible(operation)) return;
    issues.push({ verb, field, operation, problem: 'operation_unavailable' });
  }
}

function verifierQualificationRuntime(
  issues: SuperDocCapabilityContractIssue[],
  snapshot: Record<string, unknown>,
): void {
  if (!hasOwn(snapshot, 'runtimeQualification')) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: null, problem: 'runtime_qualification_missing' });
    return;
  }
  const qualification = snapshot.runtimeQualification;
  if (!isRecord(qualification)) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: null, problem: 'runtime_qualification_invalid' });
    return;
  }

  verifierProjections(issues, qualification.projections);
  verifierPlanDeMutation(issues, qualification.mutationPlans);
  verifierRemplacementDuCorps(issues, qualification.bodyReplacement);
  verifierMutationsSuivies(issues, qualification.trackedMutations);
}

function verifierProjections(issues: SuperDocCapabilityContractIssue[], value: unknown): void {
  if (!isRecord(value)) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'projections', problem: 'runtime_qualification_invalid' });
    return;
  }
  for (const projection of ['final', 'original', 'redline'] as const) {
    if (value[projection] !== 'supported') {
      issues.push({
        verb: null,
        field: 'runtimeQualification',
        operation: `projections.${projection}`,
        problem: 'runtime_requirement_unmet',
      });
    }
  }
}

function verifierPlanDeMutation(issues: SuperDocCapabilityContractIssue[], value: unknown): void {
  if (!isRecord(value)) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'mutationPlans', problem: 'runtime_qualification_invalid' });
    return;
  }
  if (value.previewAvailable !== true) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'mutationPlans.previewAvailable', problem: 'runtime_requirement_unmet' });
  }
  if (value.applyAvailable !== true) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'mutationPlans.applyAvailable', problem: 'runtime_requirement_unmet' });
  }
  const supportedStepOps = sortedOperationNames(value.supportedStepOps);
  const executedStepOps = sortedOperationNames(value.executedStepOps);
  if (supportedStepOps === null) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'mutationPlans.supportedStepOps', problem: 'runtime_qualification_invalid' });
  } else if (supportedStepOps.length === 0) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'mutationPlans.supportedStepOps', problem: 'runtime_requirement_unmet' });
  }
  if (executedStepOps === null) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'mutationPlans.executedStepOps', problem: 'runtime_qualification_invalid' });
  } else if (executedStepOps.length === 0 || (supportedStepOps !== null && supportedStepOps.length > 0
    && executedStepOps.join('\u0000') !== supportedStepOps.join('\u0000'))) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'mutationPlans.executedStepOps', problem: 'runtime_requirement_unmet' });
  }
}

function verifierRemplacementDuCorps(issues: SuperDocCapabilityContractIssue[], value: unknown): void {
  if (!isRecord(value)) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'bodyReplacement', problem: 'runtime_qualification_invalid' });
    return;
  }
  verifierMesureDeRemplacement(issues, value.direct, 'direct', 'supported');
  verifierMesureDeRemplacement(issues, value.tracked, 'tracked', 'unsupported');
}

function verifierMesureDeRemplacement(
  issues: SuperDocCapabilityContractIssue[],
  value: unknown,
  mode: 'direct' | 'tracked',
  verdictExpected: SuperDocRuntimeVerdict,
): void {
  if (!isRecord(value) || !nullableString(value.outcome) || !nullableString(value.failureCode)) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: `bodyReplacement.${mode}`, problem: 'runtime_qualification_invalid' });
    return;
  }
  if (value.verdict !== verdictExpected) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: `bodyReplacement.${mode}.verdict`, problem: 'runtime_requirement_unmet' });
  }
}

function verifierMutationsSuivies(issues: SuperDocCapabilityContractIssue[], value: unknown): void {
  if (!isRecord(value)) {
    issues.push({ verb: null, field: 'runtimeQualification', operation: 'trackedMutations', problem: 'runtime_qualification_invalid' });
    return;
  }
  for (const operation of OPERATIONS_SUIVIES_OBLIGATOIRES) {
    if (!hasOwn(value, operation)) {
      issues.push({
        verb: null,
        field: 'runtimeQualification',
        operation: `trackedMutations.${operation}`,
        problem: 'runtime_qualification_missing',
      });
      continue;
    }
    if (value[operation] !== 'tracked') {
      issues.push({
        verb: null,
        field: 'runtimeQualification',
        operation: `trackedMutations.${operation}`,
        problem: 'runtime_requirement_unmet',
      });
    }
  }
}

interface BuildSuperDocCapabilitySnapshotInput {
  catalogue: readonly CanvasCapabilityRequirement[];
  superdocVersion: string;
  measuredAt: string;
  providerOperations: Readonly<Record<string, SuperDocOperationCapability>>;
  runtimeQualification?: SuperDocRuntimeQualification;
}

/** Extrait un relevé stable et trié sans inventer les valeurs du fournisseur. */
export function buildSuperDocCapabilitySnapshot({
  catalogue,
  superdocVersion,
  measuredAt,
  providerOperations,
  runtimeQualification,
}: BuildSuperDocCapabilitySnapshotInput): SuperDocCapabilitySnapshot {
  const operationNames = new Set<string>();
  for (const definition of catalogue) {
    definition.requiredOperations.forEach((operation) => operationNames.add(operation));
    definition.reviewedOperations.forEach((operation) => operationNames.add(operation));
  }

  const operations: Record<string, SuperDocOperationCapability> = {};
  for (const operation of [...operationNames].sort()) {
    if (!hasOwn(providerOperations, operation)) continue;
    const published = providerOperations[operation];
    if (!published) continue;
    const focused: SuperDocOperationCapability = {};
    if (typeof published.available === 'boolean') focused.available = published.available;
    if (typeof published.tracked === 'boolean') focused.tracked = published.tracked;
    if (typeof published.dryRun === 'boolean') focused.dryRun = published.dryRun;
    operations[operation] = focused;
  }

  return {
    superdocVersion,
    measuredAt,
    operations,
    ...(runtimeQualification === undefined ? {} : { runtimeQualification }),
  };
}

/** Rend les écarts directement exploitables dans la sortie Vitest/Playwright. */
export function formatSuperDocCapabilityContractIssues(
  issues: readonly SuperDocCapabilityContractIssue[],
): string {
  return `Contrat de capacités SuperDoc invalide :\n${JSON.stringify(issues, null, 2)}`;
}
